/**
 * usePaginatedQuery — TanStack Query wrapper for paginated list endpoints.
 *
 * Returns the full BackendPaginatedResponse (items + pagination metadata), unlike
 * useApiQuery which unwraps to just the data field. Keeps placeholder data
 * between page transitions for smooth UX.
 */

import type { ApiResponse, AxiosRequestConfig, BackendPaginatedResponse } from "@plerity/data-access";
import { keepPreviousData, type UseQueryOptions, useQuery } from "@tanstack/react-query";
import { apiClient } from "./useApiQuery";

export interface PaginationParams {
  page?: number;
  page_size?: number;
  [key: string]: unknown;
}

/**
 * Fetch a paginated list endpoint with smooth page transitions.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePaginatedQuery<Contact>(
 *   crmKeys.list('contacts', { page, search }),
 *   '/api/contacts',
 *   { page, page_size: 25, search },
 * );
 * // data.items, data.total, data.page, data.total_pages
 * ```
 */
export function usePaginatedQuery<T>(
  queryKey: readonly unknown[],
  url: string,
  params?: PaginationParams,
  options?: Omit<UseQueryOptions<BackendPaginatedResponse<T>>, "queryKey" | "queryFn"> & {
    axiosConfig?: AxiosRequestConfig;
  }
) {
  const { axiosConfig, ...queryOptions } = options ?? {};

  return useQuery<BackendPaginatedResponse<T>>({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<BackendPaginatedResponse<T>>>(url, {
        params,
        ...axiosConfig,
      });
      if (!response.data.success) {
        throw new Error(response.data.error ?? response.data.message ?? "Request failed");
      }
      return response.data.data;
    },
    placeholderData: keepPreviousData,
    ...queryOptions,
  });
}
